// STEP 5-CS · 판매자 CS 문의센터 (탑레벨 섹션)
// 기존 API 100% REUSE (신규 API 없음 · DB 변경 없음):
//   GET  /api/seller/inquiries?status=&category=&q=
//   GET  /api/seller/inquiries/summary
//   GET  /api/seller/inquiries/{inquiry_id}      (related_order enrich 포함)
//   POST /api/seller/inquiries/{inquiry_id}/answer      { content }
//   POST /api/seller/inquiries/{inquiry_id}/quick-reply { content, status }
//   PATCH /api/seller/inquiries/{inquiry_id}/status     { status }
// 카테고리 8종 · 상태 4종은 백엔드 whitelist 와 완전히 동일해야 함 (phase12_customers._create_inquiry.ALLOWED).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  MessageCircle, Search, X, Send, Package as PackageIcon, ShoppingBag,
  User as UserIcon, Phone, ChevronRight, RefreshCw, Radio, Calendar as CalendarIcon,
} from 'lucide-react';
import api from '@/lib/api';
import { getTrackingAvailable, getTrackingUrl, getTrackingStatus } from '@/lib/trackingUrl';

// ── 상수 (백엔드 whitelist 와 동일하게 유지) ─────────────────────────────────
const CATEGORY_MAP = {
  product:  '상품문의',
  order:    '주문문의',
  payment:  '결제문의',
  shipping: '배송문의',
  exchange: '교환문의',
  return:   '반품문의',
  cancel:   '취소문의',
  other:    '기타문의',
};
const CATEGORY_ORDER = ['product', 'order', 'payment', 'shipping', 'exchange', 'return', 'cancel', 'other'];

const STATUS_TABS = [
  { key: '',            label: '전체',       accent: 'bg-black text-white' },
  { key: 'new',         label: '답변대기',   accent: 'bg-[#FF3B30] text-white' },
  { key: 'in_progress', label: '처리중',     accent: 'bg-amber-500 text-white' },
  { key: 'answered',    label: '답변완료',   accent: 'bg-emerald-500 text-white' },
  { key: 'closed',      label: '종료',       accent: 'bg-gray-500 text-white' },
];
const STATUS_LABEL = { new: '답변대기', in_progress: '처리중', answered: '답변완료', closed: '종료' };
const STATUS_COLOR = {
  new:         'bg-[#FF3B30] text-white',
  in_progress: 'bg-amber-500 text-white',
  answered:    'bg-emerald-500 text-white',
  closed:      'bg-gray-500 text-white',
};

const fmt = (d) => { try { return new Date(d).toLocaleString('ko-KR', { hour12: false }); } catch { return '-'; } };
const fmtDay = (d) => { try { return new Date(d).toLocaleDateString('ko-KR'); } catch { return '-'; } };
const maskPhone = (p) => {
  if (!p) return '-';
  const s = String(p).replace(/\D/g, '');
  if (s.length < 8) return '***';
  return `${s.slice(0, 3)}-****-${s.slice(-4)}`;
};

// STEP 5-CS-D · Frontend-only SLA helpers · 기존 created_at/answered_at/status 만 사용
const isUnanswered = (r) => (r?.status === 'new' || r?.status === 'in_progress') && !r?.answered_at;
const computeWaitMs = (createdAt) => {
  if (!createdAt) return null;
  const t = Date.parse(createdAt);
  if (Number.isNaN(t)) return null;
  return Math.max(0, Date.now() - t);
};
const slaTier = (ms) => {
  if (ms == null) return null;
  const minutes = ms / 60000;
  if (minutes < 10)  return { label: '방금 접수',                          cls: 'bg-emerald-500/10 text-emerald-600' };
  if (minutes < 60)  return { label: `${Math.floor(minutes)}m 대기`,       cls: 'bg-sky-500/10 text-sky-600' };
  const hours = minutes / 60;
  if (hours < 3)     return { label: `${Math.floor(hours)}h 대기`,         cls: 'bg-amber-500/10 text-amber-600' };
  if (hours < 24)    return { label: `${Math.floor(hours)}h 대기`,         cls: 'bg-orange-500 text-white' };
  const days = hours / 24;
  return { label: `${Math.floor(days)}d 대기 · SLA 초과`, cls: 'bg-[#FF3B30] text-white animate-pulse' };
};

// ── 문의 상세 Drawer / Modal ────────────────────────────────────────────────
const InquiryDetailDrawer = ({ inquiryId, onClose, onChanged }) => {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [nextStatus, setNextStatus] = useState('answered');
  // KAKAO V1 · CS 문의 답변 건별 카카오톡 발송 토글 (default OFF).
  const [notifyKakao, setNotifyKakao] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!inquiryId) return;
    setLoading(true);
    try {
      const r = await api.get(`/seller/inquiries/${inquiryId}`);
      setD(r.data);
      setNextStatus(r.data?.status === 'new' ? 'answered' : (r.data?.status || 'answered'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || '문의 조회 실패');
      onClose?.();
    } finally { setLoading(false); }
  }, [inquiryId, onClose]);

  useEffect(() => { load(); }, [load]);

  const submitReply = async () => {
    if (!reply.trim()) { toast.error('답변 내용을 입력해주세요.'); return; }
    setSaving(true);
    try {
      // Quick-reply: 답변 + 상태 동시 변경 (phase51_unified · 기존 endpoint REUSE)
      await api.post(`/seller/inquiries/${inquiryId}/quick-reply`, {
        content: reply.trim(),
        status: nextStatus,
        notify_kakao: notifyKakao,   // KAKAO V1 · 건별 발송 토글
      });
      toast.success(notifyKakao ? '답변 저장 · 카카오톡 발송 요청됨' : '답변이 저장되었습니다.');
      setReply('');
      setNotifyKakao(false);         // 다음 답변을 위해 안전하게 default OFF 로 리셋
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || '답변 저장 실패'); }
    finally { setSaving(false); }
  };

  const changeStatus = async (status) => {
    setSaving(true);
    try {
      await api.patch(`/seller/inquiries/${inquiryId}/status`, { status });
      toast.success(`상태가 "${STATUS_LABEL[status]}"로 변경되었습니다.`);
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || '상태 변경 실패'); }
    finally { setSaving(false); }
  };

  if (!inquiryId) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-stretch justify-end sm:items-center sm:justify-center p-0 sm:p-4"
         onClick={onClose} data-testid="inq-detail-drawer">
      <div onClick={(e) => e.stopPropagation()}
           className="bg-white dark:bg-[#141414] w-full sm:max-w-2xl sm:rounded-2xl sm:h-auto h-full max-h-screen sm:max-h-[92vh] overflow-y-auto p-4 sm:p-6 space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-10"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF3B30]"/></div>
        ) : d ? (
          <>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${STATUS_COLOR[d.status] || 'bg-gray-300'}`} data-testid="inq-detail-status">{STATUS_LABEL[d.status] || d.status}</span>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-black/5 dark:bg-white/10" data-testid="inq-detail-category">{CATEGORY_MAP[d.category] || d.category}</span>
                  {d.priority && d.priority !== 'normal' && <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-[#FF3B30]/10 text-[#FF3B30]">우선</span>}
                  {d.visibility === 'public' && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-600">공개</span>}
                </div>
                <h3 className="mt-2 text-lg font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="inq-detail-title">
                  {d.title || '(제목 없음)'}
                </h3>
                <p className="text-[11px] text-gray-500 mt-0.5">등록 {fmt(d.created_at)}{d.answered_at ? ` · 답변 ${fmt(d.answered_at)}` : ''}</p>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-[#FF3B30] p-1" aria-label="닫기" data-testid="inq-detail-close">
                <X size={20}/>
              </button>
            </div>

            {/* 본문 */}
            <div className="rounded-lg border border-black/5 dark:border-white/10 p-3 bg-black/2 dark:bg-white/5">
              <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">문의 내용</p>
              <p className="text-sm whitespace-pre-wrap break-words" data-testid="inq-detail-content">{d.content || '-'}</p>
            </div>

            {/* 고객 · 주문 · 상품 · 방송 연결 */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div className="rounded-lg border border-black/5 dark:border-white/10 p-3">
                <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 inline-flex items-center gap-1"><UserIcon size={10}/> 고객</p>
                <p className="text-sm font-bold mt-1 truncate" data-testid="inq-detail-buyer-name">{d.buyer_snapshot?.name || '-'}</p>
                <p className="text-[11px] text-gray-500 inline-flex items-center gap-1"><Phone size={10}/> {maskPhone(d.buyer_snapshot?.phone)}</p>
                {d.buyer_snapshot?.email && <p className="text-[10px] text-gray-400 truncate">{d.buyer_snapshot.email}</p>}
              </div>
              <div className="rounded-lg border border-black/5 dark:border-white/10 p-3">
                <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 inline-flex items-center gap-1"><ShoppingBag size={10}/> 주문</p>
                {d.order_number ? (
                  <>
                    <p className="text-sm font-mono font-bold mt-1 truncate" data-testid="inq-detail-order-number">{d.order_number}</p>
                    {d.related_order && (
                      <p className="text-[11px] text-gray-500 truncate">{d.related_order.status_label || d.related_order.status || '-'}</p>
                    )}
                    {/* STEP 5-CT-C · 배송/발송 정보 (기존 shipment 필드 REUSE) */}
                    {d.related_order && (d.related_order.shipment?.courier || d.related_order.shipment?.tracking_number || d.related_order.shipment?.shipped_at || d.related_order.shipment?.delivered_at || d.related_order.delivery_status) && (
                      <div className="mt-2 pt-2 border-t border-black/5 dark:border-white/10 space-y-0.5" data-testid="inq-detail-shipment">
                        {d.related_order.shipment?.courier && (
                          <p className="text-[11px]"><span className="text-gray-500">택배사:</span> <b>{d.related_order.shipment.courier}</b></p>
                        )}
                        {d.related_order.shipment?.tracking_number && (
                          <p className="text-[11px]"><span className="text-gray-500">송장:</span> <span className="font-mono font-bold">{d.related_order.shipment.tracking_number}</span></p>
                        )}
                        {d.related_order.delivery_status && (
                          <p className="text-[11px]"><span className="text-gray-500">배송상태:</span> <b>{d.related_order.delivery_status}</b></p>
                        )}
                        {d.related_order.shipment?.shipped_at && (
                          <p className="text-[11px]"><span className="text-gray-500">발송일:</span> <b>{fmt(d.related_order.shipment.shipped_at)}</b></p>
                        )}
                        {d.related_order.shipment?.delivered_at && (
                          <p className="text-[11px] text-emerald-600 dark:text-emerald-400">배송완료: <b>{fmt(d.related_order.shipment.delivered_at)}</b></p>
                        )}
                      </div>
                    )}
                    {/* STEP 5-CT-C · 다중 shipments[] · 있을 때만 */}
                    {d.related_order && Array.isArray(d.related_order.shipments) && d.related_order.shipments.length > 1 && (
                      <div className="mt-2 pt-2 border-t border-black/5 dark:border-white/10 space-y-1" data-testid="inq-detail-multi-shipments">
                        {d.related_order.shipments.map((sh, si) => (
                          <div key={sh.shipment_id || si} className="text-[10px] text-gray-500 px-2 py-1 rounded bg-black/[0.03] dark:bg-white/[0.05]">
                            배송 {sh.shipment_no || si + 1} · <b>{sh.courier || '-'}</b>
                            {sh.tracking_number && <> · <span className="font-mono">{sh.tracking_number}</span></>}
                            {sh.status && <> · {sh.status}</>}
                          </div>
                        ))}
                      </div>
                    )}
                    {/* STEP 5-CT-D · 배송조회 준비 UI (외부 API 미연동 · 공식 URL 정적 매핑만) */}
                    {d.related_order && (d.related_order.shipment?.courier || d.related_order.shipment?.tracking_number) && (() => {
                      const sh = d.related_order.shipment || {};
                      const status = getTrackingStatus({ shipment: sh, delivery_status: d.related_order.delivery_status, orderStatus: d.related_order.status });
                      const available = getTrackingAvailable(sh.courier, sh.tracking_number);
                      const url = available ? getTrackingUrl(sh.courier, sh.tracking_number) : null;
                      return (
                        <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="inq-detail-tracking">
                          {status && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-black/5 dark:bg-white/10 font-bold" data-testid="inq-detail-tracking-status">{status}</span>}
                          {available && url ? (
                            <a href={url} target="_blank" rel="noreferrer noopener"
                               className="text-[11px] font-bold text-[#FF3B30] underline underline-offset-2 hover:text-[#e0332a]"
                               data-testid="inq-detail-tracking-link">
                              배송조회 열기 ↗
                            </a>
                          ) : (
                            <span className="text-[10px] text-gray-400" data-testid="inq-detail-tracking-pending">
                              배송조회 준비중
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </>
                ) : <p className="text-xs text-gray-400 mt-1">연결 주문 없음</p>}
              </div>
              <div className="rounded-lg border border-black/5 dark:border-white/10 p-3">
                <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 inline-flex items-center gap-1"><PackageIcon size={10}/> 상품 · 방송</p>
                {d.product_snapshot?.name ? (
                  <p className="text-sm font-bold mt-1 truncate" data-testid="inq-detail-product-name">{d.product_snapshot.name}</p>
                ) : <p className="text-xs text-gray-400 mt-1">연결 상품 없음</p>}
                {d.broadcast_id && (
                  <p className="text-[11px] text-gray-500 inline-flex items-center gap-1 mt-0.5"><Radio size={10}/> {d.broadcast_id}</p>
                )}
              </div>
            </div>

            {/* 첨부 (있을 때만) · STEP 5-CS-B: 이미지 URL 은 썸네일 렌더 */}
            {Array.isArray(d.attachments) && d.attachments.length > 0 && (
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1.5">첨부 ({d.attachments.length})</p>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5">
                  {d.attachments.map((a, i) => {
                    const href = typeof a === 'string' ? a : (a?.url || '');
                    const mime = typeof a === 'object' ? (a?.mime || '') : '';
                    const isImage = mime.startsWith('image/') || /\.(jpe?g|png|webp|gif)$/i.test(href) || /\/api\/files\//.test(href);
                    if (!href) return null;
                    return isImage ? (
                      <a key={i} href={href} target="_blank" rel="noreferrer"
                         className="relative aspect-square rounded-lg overflow-hidden border border-black/10 dark:border-white/10 bg-black/5 dark:bg-white/5 hover:border-[#FF3B30]/60"
                         data-testid={`inq-detail-attach-${i}`}>
                        <img src={href} alt={`첨부 ${i+1}`} className="w-full h-full object-cover" loading="lazy"/>
                      </a>
                    ) : (
                      <a key={i} href={href} target="_blank" rel="noreferrer"
                         className="text-[11px] text-[#FF3B30] hover:underline truncate max-w-[200px] inline-flex items-center px-2 py-1 rounded border border-black/10 dark:border-white/10"
                         data-testid={`inq-detail-attach-${i}`}>
                        첨부 #{i+1}
                      </a>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 답변 이력 */}
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1.5">답변 이력 ({(d.answers || []).length})</p>
              {(d.answers || []).length === 0 ? (
                <p className="text-xs text-gray-400 py-2">아직 답변이 없습니다.</p>
              ) : (
                <div className="space-y-2" data-testid="inq-detail-answers">
                  {d.answers.map((a) => (
                    <div key={a.answer_id} className="rounded-lg border border-emerald-500/30 bg-emerald-50/60 dark:bg-emerald-500/10 p-2.5">
                      <p className="text-[10px] text-gray-500">
                        <span className="font-bold text-emerald-700 dark:text-emerald-300">{a.author?.name || a.author?.user_id || '판매자'}</span> · {fmt(a.created_at)}
                      </p>
                      <p className="text-sm whitespace-pre-wrap break-words mt-1">{a.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Quick-reply Form */}
            <div className="rounded-lg border border-[#FF3B30]/30 bg-[#FF3B30]/5 p-3 space-y-2" data-testid="inq-detail-quickreply">
              <p className="text-[10px] font-black uppercase tracking-widest text-[#FF3B30]">답변 · 상태 변경</p>
              <textarea
                rows={3}
                placeholder="답변 또는 처리 메모를 입력하세요."
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                className="w-full px-2.5 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm"
                data-testid="inq-detail-reply-content"
              />
              <div className="flex items-center gap-2 flex-wrap">
                <select value={nextStatus} onChange={(e) => setNextStatus(e.target.value)}
                        className="px-2 py-1.5 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs"
                        data-testid="inq-detail-reply-status">
                  <option value="new">답변대기</option>
                  <option value="in_progress">처리중</option>
                  <option value="answered">답변완료</option>
                  <option value="closed">종료</option>
                </select>
                <button onClick={submitReply} disabled={saving}
                        className="flex-1 min-w-[120px] py-1.5 bg-[#FF3B30] hover:bg-[#D32F2F] text-white text-xs font-black rounded inline-flex items-center justify-center gap-1 disabled:opacity-60"
                        data-testid="inq-detail-reply-submit">
                  <Send size={12}/> {saving ? '저장 중…' : '답변 저장 · 상태 변경'}
                </button>
              </div>
              {/* KAKAO V1 · 건별 카카오톡 발송 토글 (default OFF · 승인 템플릿 LM_INQUIRY_ANSWERED) */}
              <label className="flex items-start gap-2 pt-1 cursor-pointer"
                     data-testid="inq-detail-notify-kakao-label">
                <input type="checkbox"
                       checked={notifyKakao}
                       onChange={(e) => setNotifyKakao(e.target.checked)}
                       disabled={saving || !reply.trim()}
                       className="mt-0.5"
                       data-testid="inq-detail-notify-kakao"/>
                <span className="text-[11px] text-gray-700 dark:text-gray-200 leading-snug">
                  이 답변을 <b>고객에게 카카오 알림톡으로 발송</b> (승인 템플릿 <code>LM_INQUIRY_ANSWERED</code>)
                  <br/>
                  <span className="text-[10px] text-gray-500">체크 시 답변 저장과 함께 BizM 실 발송 · 미체크 시 답변만 저장</span>
                </span>
              </label>
              <div className="flex items-center gap-1 flex-wrap pt-1">
                <span className="text-[10px] text-gray-500 mr-1">상태만 변경:</span>
                {['new', 'in_progress', 'answered', 'closed'].filter((s) => s !== d.status).map((s) => (
                  <button key={s} onClick={() => changeStatus(s)} disabled={saving}
                          className="text-[10px] font-bold px-2 py-0.5 rounded-full border border-black/10 dark:border-white/10 hover:border-[#FF3B30] disabled:opacity-60"
                          data-testid={`inq-detail-set-status-${s}`}>
                    → {STATUS_LABEL[s]}
                  </button>
                ))}
              </div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

// ── 메인 컴포넌트 ────────────────────────────────────────────────────────────
const InquiriesCenter = () => {
  const [summary, setSummary] = useState({ total: 0, new: 0, in_progress: 0, answered: 0, closed: 0 });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [q, setQ] = useState('');
  const [qInput, setQInput] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [openId, setOpenId] = useState(null);
  // STEP 5-CS-D · 60초마다 재계산해 SLA 배지 라이브 업데이트 (신규 API 호출 없음)
  const [, setSlaTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setSlaTick((v) => v + 1), 60_000);
    return () => clearInterval(t);
  }, []);

  const loadSummary = useCallback(async () => {
    try { const r = await api.get('/seller/inquiries/summary'); setSummary(r.data || {}); }
    catch { /* silent · 요약이 실패해도 리스트는 동작 */ }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (statusFilter) params.status = statusFilter;
      if (categoryFilter) params.category = categoryFilter;
      if (q.trim()) params.q = q.trim();
      const r = await api.get('/seller/inquiries', { params });
      setRows(r.data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'CS 문의 목록 불러오기 실패'); }
    finally { setLoading(false); }
  }, [statusFilter, categoryFilter, q]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadList(); }, [loadList]);

  // 기간 필터는 클라이언트에서 (백엔드 date_from/to 미지원 · API 변경 금지 원칙 준수)
  const filtered = useMemo(() => {
    if (!dateFrom && !dateTo) return rows;
    const from = dateFrom ? new Date(dateFrom + 'T00:00:00') : null;
    const to = dateTo ? new Date(dateTo + 'T23:59:59') : null;
    return rows.filter((r) => {
      const t = r.created_at ? new Date(r.created_at) : null;
      if (!t) return false;
      if (from && t < from) return false;
      if (to && t > to) return false;
      return true;
    });
  }, [rows, dateFrom, dateTo]);

  const submitSearch = (e) => { e?.preventDefault?.(); setQ(qInput); };
  const resetFilters = () => {
    setStatusFilter(''); setCategoryFilter('');
    setDateFrom(''); setDateTo('');
    setQ(''); setQInput('');
  };

  return (
    <div className="space-y-4" data-testid="inquiries-center">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="inline-flex items-center gap-2">
          <MessageCircle className="text-[#FF3B30]" size={22}/>
          <h2 className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="inquiries-center-title">
            CS 문의센터
          </h2>
          <span className="text-sm text-gray-400" data-testid="inquiries-center-total">전체 {summary.total || 0}건</span>
        </div>
        <button onClick={() => { loadSummary(); loadList(); }}
                className="text-[11px] font-bold text-[#FF3B30] hover:underline inline-flex items-center gap-1"
                data-testid="inquiries-refresh">
          <RefreshCw size={12}/> 새로고침
        </button>
      </div>

      {/* Status Tabs */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" data-testid="inquiries-status-tabs">
        {STATUS_TABS.map((t) => {
          const count = t.key === '' ? (summary.total || 0) : (summary[t.key] || 0);
          const active = statusFilter === t.key;
          return (
            <button
              key={t.key || 'all'}
              onClick={() => setStatusFilter(t.key)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-black inline-flex items-center gap-1.5 transition-colors ${
                active ? t.accent : 'bg-black/5 dark:bg-white/10 hover:bg-black/10'
              }`}
              data-testid={`inquiries-tab-${t.key || 'all'}`}
            >
              {t.label}
              <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-full tabular-nums ${
                active ? 'bg-white/20 text-white' : 'bg-white dark:bg-[#141414] text-gray-600'
              }`}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Filter Bar */}
      <form onSubmit={submitSearch} className="rounded-xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] p-3 grid grid-cols-1 sm:grid-cols-4 gap-2" data-testid="inquiries-filter-bar">
        <label className="col-span-1">
          <span className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">카테고리</span>
          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
                  className="w-full px-2 py-1.5 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs"
                  data-testid="inquiries-filter-category">
            <option value="">전체</option>
            {CATEGORY_ORDER.map((k) => <option key={k} value={k}>{CATEGORY_MAP[k]}</option>)}
          </select>
        </label>
        <label>
          <span className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1 inline-flex items-center gap-1"><CalendarIcon size={10}/> 시작일</span>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                 className="w-full px-2 py-1.5 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs"
                 data-testid="inquiries-filter-date-from" />
        </label>
        <label>
          <span className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1 inline-flex items-center gap-1"><CalendarIcon size={10}/> 종료일</span>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                 className="w-full px-2 py-1.5 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs"
                 data-testid="inquiries-filter-date-to" />
        </label>
        <label>
          <span className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">검색 (제목·내용·주문번호·이름·전화)</span>
          <div className="flex items-stretch gap-1">
            <input type="text" value={qInput} onChange={(e) => setQInput(e.target.value)}
                   placeholder="예: 주문번호 · 홍길동 · 010-…"
                   className="flex-1 min-w-0 px-2 py-1.5 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs"
                   data-testid="inquiries-filter-q" />
            <button type="submit" className="px-3 py-1.5 rounded bg-[#FF3B30] hover:bg-[#D32F2F] text-white text-xs font-black inline-flex items-center gap-1" data-testid="inquiries-filter-submit">
              <Search size={12}/>
            </button>
          </div>
        </label>
        {(statusFilter || categoryFilter || dateFrom || dateTo || q) && (
          <div className="sm:col-span-4 flex justify-end">
            <button type="button" onClick={resetFilters} className="text-[11px] font-bold text-gray-500 hover:text-[#FF3B30] inline-flex items-center gap-0.5" data-testid="inquiries-filter-reset">
              <X size={10}/> 필터 초기화
            </button>
          </div>
        )}
      </form>

      {/* List */}
      <div className="rounded-xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] overflow-hidden" data-testid="inquiries-list-wrap">
        {loading ? (
          <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF3B30]"/></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 px-4" data-testid="inquiries-list-empty">
            <MessageCircle className="mx-auto text-gray-300 mb-2" size={32}/>
            <p className="text-sm font-bold text-gray-500">조건에 맞는 문의가 없습니다.</p>
            <p className="text-xs text-gray-400 mt-1">필터를 초기화하거나, 새로 등록되는 문의를 기다려주세요.</p>
          </div>
        ) : (
          <ul className="divide-y divide-black/5 dark:divide-white/10" data-testid="inquiries-list">
            {filtered.map((r) => (
              <li key={r.inquiry_id}>
                <button
                  type="button"
                  onClick={() => setOpenId(r.inquiry_id)}
                  className="w-full text-left px-3 py-3 sm:px-4 sm:py-3 flex items-start gap-3 hover:bg-black/2 dark:hover:bg-white/5 transition-colors"
                  data-testid={`inquiries-row-${r.inquiry_id}`}
                >
                  <div className="flex flex-col items-start gap-1 min-w-[64px] flex-shrink-0">
                    <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${STATUS_COLOR[r.status] || 'bg-gray-300'}`}>
                      {STATUS_LABEL[r.status] || r.status}
                    </span>
                    <span className="text-[9px] font-bold text-gray-500">{CATEGORY_MAP[r.category] || r.category}</span>
                    {isUnanswered(r) && (() => {
                      const tier = slaTier(computeWaitMs(r.created_at));
                      if (!tier) return null;
                      return (
                        <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full whitespace-nowrap ${tier.cls}`}
                              data-testid={`inquiries-row-sla-${r.inquiry_id}`}>
                          {tier.label}
                        </span>
                      );
                    })()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className="font-bold text-sm truncate" data-testid={`inquiries-row-title-${r.inquiry_id}`}>{r.title || '(제목 없음)'}</p>
                      {(r.answers || []).length > 0 && (
                        <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600">답변 {r.answers.length}</span>
                      )}
                    </div>
                    {r.content && <p className="text-xs text-gray-500 line-clamp-1 mt-0.5">{r.content}</p>}
                    <div className="flex items-center gap-2 flex-wrap text-[10px] text-gray-400 mt-1">
                      {r.buyer_snapshot?.name && <span className="inline-flex items-center gap-0.5"><UserIcon size={9}/> {r.buyer_snapshot.name}</span>}
                      {r.buyer_snapshot?.phone && <span className="inline-flex items-center gap-0.5"><Phone size={9}/> {maskPhone(r.buyer_snapshot.phone)}</span>}
                      {r.order_number && <span className="inline-flex items-center gap-0.5 font-mono"><ShoppingBag size={9}/> {r.order_number}</span>}
                      {r.product_snapshot?.name && <span className="inline-flex items-center gap-0.5 truncate max-w-[160px]"><PackageIcon size={9}/> {r.product_snapshot.name}</span>}
                      <span className="ml-auto">{fmtDay(r.created_at)}</span>
                    </div>
                  </div>
                  <ChevronRight size={16} className="text-gray-300 flex-shrink-0 mt-1"/>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Detail Drawer */}
      {openId && (
        <InquiryDetailDrawer
          inquiryId={openId}
          onClose={() => setOpenId(null)}
          onChanged={() => { loadSummary(); loadList(); }}
        />
      )}
    </div>
  );
};

export default InquiriesCenter;
